import { useState } from "react";

interface Member {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  credit: number;
  plan: string;
  total_sent: number;
  total_charged: number;
  per_message_rate: number | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  members: Member[];
  loading: boolean;
  onRefresh: () => void;
}

export default function SubMemberTable({ members, loading, onRefresh }: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filtered = statusFilter === "all" ? members : members.filter((m) => m.status === statusFilter);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((m) => m.id)));
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-medium border border-emerald-500/20 whitespace-nowrap">활성</span>;
      case "inactive":
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-400 text-xs font-medium border border-gray-500/20 whitespace-nowrap">비활성</span>;
      case "suspended":
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 text-xs font-medium border border-red-500/20 whitespace-nowrap">정지</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/[0.06] text-gray-400 text-xs font-medium border border-white/[0.08] whitespace-nowrap">{status}</span>;
    }
  };

  const planBadge = (plan: string) => {
    const planName = plan === "FREE" ? "무료" : plan === "BASIC" ? "베이직" : plan === "PRO" ? "프로" : plan === "ENTERPRISE" ? "엔터프라이즈" : plan;
    return <span className="text-xs text-gray-400 whitespace-nowrap">{planName}</span>;
  };

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
        <div>
          <h3 className="text-white font-semibold text-base">하위 회원 목록</h3>
          <p className="text-gray-500 text-xs mt-0.5">{members.length}명의 회원이 등록되어 있습니다</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/[0.05] border border-white/[0.1] text-gray-300 text-xs rounded-lg px-3 py-2 cursor-pointer outline-none focus:border-amber-500/40"
          >
            <option value="all">전체 상태</option>
            <option value="active">활성</option>
            <option value="inactive">비활성</option>
            <option value="suspended">정지</option>
          </select>
          <button
            onClick={onRefresh}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] text-gray-400 hover:text-white hover:bg-white/[0.08] cursor-pointer transition-colors"
            title="새로고침"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="px-4 py-3 w-10">
                <button
                  onClick={toggleAll}
                  className="w-4 h-4 flex items-center justify-center rounded border border-white/[0.2] cursor-pointer transition-colors hover:border-amber-500/40"
                >
                  {selectedIds.size === filtered.length && filtered.length > 0 && (
                    <i className="ri-check-line text-amber-400 text-xs"></i>
                  )}
                </button>
              </th>
              <th className="px-4 py-3 text-gray-500 text-xs font-medium whitespace-nowrap">회원</th>
              <th className="px-4 py-3 text-gray-500 text-xs font-medium whitespace-nowrap">상태</th>
              <th className="px-4 py-3 text-gray-500 text-xs font-medium whitespace-nowrap">플랜</th>
              <th className="px-4 py-3 text-gray-500 text-xs font-medium whitespace-nowrap text-right">크레딧</th>
              <th className="px-4 py-3 text-gray-500 text-xs font-medium whitespace-nowrap text-right">총 발송</th>
              <th className="px-4 py-3 text-gray-500 text-xs font-medium whitespace-nowrap text-right">단가</th>
              <th className="px-4 py-3 text-gray-500 text-xs font-medium whitespace-nowrap">가입일</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-white/[0.04]">
                  <td className="px-4 py-3"><div className="w-4 h-4 bg-white/[0.06] rounded animate-pulse"></div></td>
                  <td className="px-4 py-3"><div className="w-24 h-4 bg-white/[0.06] rounded animate-pulse"></div></td>
                  <td className="px-4 py-3"><div className="w-12 h-4 bg-white/[0.06] rounded animate-pulse"></div></td>
                  <td className="px-4 py-3"><div className="w-12 h-4 bg-white/[0.06] rounded animate-pulse"></div></td>
                  <td className="px-4 py-3"><div className="w-16 h-4 bg-white/[0.06] rounded animate-pulse ml-auto"></div></td>
                  <td className="px-4 py-3"><div className="w-12 h-4 bg-white/[0.06] rounded animate-pulse ml-auto"></div></td>
                  <td className="px-4 py-3"><div className="w-12 h-4 bg-white/[0.06] rounded animate-pulse ml-auto"></div></td>
                  <td className="px-4 py-3"><div className="w-20 h-4 bg-white/[0.06] rounded animate-pulse"></div></td>
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-white/[0.04]">
                    <i className="ri-team-line text-gray-600 text-xl"></i>
                  </div>
                  <p className="text-gray-500 text-sm">등록된 하위 회원이 없습니다</p>
                </td>
              </tr>
            ) : (
              filtered.map((member) => (
                <tr
                  key={member.id}
                  className={`border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors ${selectedIds.has(member.id) ? "bg-white/[0.03]" : ""}`}
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleSelect(member.id)}
                      className={`w-4 h-4 flex items-center justify-center rounded border cursor-pointer transition-colors ${selectedIds.has(member.id) ? "border-amber-500 bg-amber-500/20" : "border-white/[0.2] hover:border-amber-500/40"}`}
                    >
                      {selectedIds.has(member.id) && <i className="ri-check-line text-amber-400 text-xs"></i>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-orange-500 text-white text-[10px] font-bold shrink-0">
                        {(member.full_name ?? member.email ?? "U").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium whitespace-nowrap">{member.full_name ?? "이름 없음"}</p>
                        <p className="text-gray-500 text-xs whitespace-nowrap">{member.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{statusBadge(member.status)}</td>
                  <td className="px-4 py-3">{planBadge(member.plan)}</td>
                  <td className="px-4 py-3 text-right text-white text-sm font-medium whitespace-nowrap">{member.credit.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-300 text-sm whitespace-nowrap">{member.total_sent.toLocaleString()}건</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {member.per_message_rate ? (
                      <span className="text-amber-400 text-sm font-medium">₩{member.per_message_rate.toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-500 text-xs">기본</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {new Date(member.created_at).toLocaleDateString("ko-KR", { year: "numeric", month: "short", day: "numeric" })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {selectedIds.size > 0 && (
        <div className="px-5 py-3 border-t border-white/[0.06] bg-amber-500/5 flex items-center justify-between">
          <span className="text-amber-400 text-xs font-medium">{selectedIds.size}명 선택됨</span>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-gray-500 text-xs hover:text-white cursor-pointer transition-colors"
          >
            선택 해제
          </button>
        </div>
      )}
    </div>
  );
}